import { ArrowTrendingUpIcon } from '@heroicons/react/24/outline'
import moment from 'moment'
import { ReactElement } from 'react'
import { SectionTitle } from 'app/components/ai-agent-voice-ops/SectionTitle'
import SimpleTable from 'app/components/table/SimpleTable'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import SegmentControl from 'app/components/viz/SegmentControl'
import { DataSourceBadge } from './DataSourceBadge'
import type { WorseningMetric } from './smsAgentOpsMetrics'
import type { UseSmsAgentOpsReturn } from './useSmsAgentOps'

const METRIC_OPTIONS: { value: WorseningMetric; label: string; noun: string }[] = [
  { value: `takeoverRate`, label: `Takeover Rate`, noun: `takeover rate` },
  { value: `unansweredRate`, label: `Unanswered Rate`, noun: `unanswered rate` },
]

type WorseningSectionProps = Pick<
  UseSmsAgentOpsReturn,
  | `worseningRows`
  | `worseningColumns`
  | `worseningMetric`
  | `setWorseningMetric`
  | `previousFromDate`
  | `previousToDate`
>

/** Locations whose takeover or unanswered rate rose against the previous period (plan D14). */
export function WorseningSection({
  worseningRows,
  worseningColumns,
  worseningMetric,
  setWorseningMetric,
  previousFromDate,
  previousToDate,
}: WorseningSectionProps): ReactElement {
  const previousPeriod = `${moment.utc(previousFromDate).format(`MMM D`)} – ${moment.utc(previousToDate).format(`MMM D, YYYY`)}`
  const noun = METRIC_OPTIONS.find((option) => option.value === worseningMetric)?.noun

  return (
    <div className="mb-8">
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <SectionTitle
          title="Getting Worse"
          badge={`${worseningRows?.length ?? 0} Locations`}
          adornment={<DataSourceBadge source="preview" />}
        />
        <SegmentControl
          label="Compare"
          buttons={METRIC_OPTIONS.map((option) => ({
            label: option.label,
            onClick: () => setWorseningMetric(option.value),
            isActive: worseningMetric === option.value,
          }))}
        />
      </div>
      <p className="mb-3 text-body-sm text-subtle">
        {`Locations whose ${noun} is higher than in the previous period (${previousPeriod}), biggest rise first.`}
      </p>
      <SimpleTable
        activeSubpage="sms-agent-ops-worsening"
        data={worseningRows}
        columns={worseningColumns}
        enableDeepLinking={false}
        hideFooter
        emptyState={
          <TableEmptyState
            icon={<ArrowTrendingUpIcon className="h-6 w-6" />}
            title="Nothing getting worse"
            description={`Every location measured in both periods held or improved its ${noun}.`}
          />
        }
      />
    </div>
  )
}
